import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Building, Users, Edit, Trash2, Plus, Printer, 
  ChevronDown, ChevronRight, X, Check, RotateCcw, Monitor
} from 'lucide-react';
import { toast } from 'sonner';
import secureStorage from '../utils/secureStorage';
import { writeSanitizedHTML } from '../utils/safePrint';

// Default ICS Organization Structure - rebuilt with aligned participant dropdown labels
const defaultOrgStructure = {
  id: 'ic',
  title: 'Command - Incident Commander',
  type: 'incident-commander',
  participantId: null,
  commandStaff: [
    { id: 'ic-deputy', title: 'Command - Incident Commander (deputy)', type: 'command-staff', participantId: null, children: [] },
    { id: 'safety', title: 'Command - Safety Officer', type: 'command-staff', participantId: null, children: [] },
    { id: 'information', title: 'Command - Information Officer', type: 'command-staff', participantId: null, children: [] },
    { id: 'liaison', title: 'Command - Liaison Officer', type: 'command-staff', participantId: null, children: [] }
  ],
  generalStaff: [
    {
      id: 'ops', title: 'Operations - Operations Chief', type: 'operations', participantId: null,
      children: [
        { id: 'staging', title: 'Operations - Staging Area Manager', type: 'operations', participantId: null, children: [] },
        {
          id: 'air-ops', title: 'Operations - Air Ops Branch Director', type: 'branch', participantId: null,
          children: [
            {
              id: 'air-support', title: 'Operations - Air Support Group Supervisor', type: 'division', participantId: null,
              children: [
                { id: 'helibase', title: 'Operations - Helibase Supervisor', type: 'operations', participantId: null, children: [] },
                { id: 'helispot', title: 'Operations - Helispot Supervisor', type: 'operations', participantId: null, children: [] },
                { id: 'fixed-wing-coordinator', title: 'Operations - Fixed-Wing Coordinator', type: 'operations', participantId: null, children: [] }
              ]
            },
            {
              id: 'air-tactical', title: 'Operations - Air Tactical Group Supervisor', type: 'division', participantId: null,
              children: [
                { id: 'fixed-wing-coord-tac', title: 'Operations - Fixed-Wing Coord (Tac)', type: 'operations', participantId: null, children: [] },
                { id: 'air-tanker-coord', title: 'Operations - Air Tanker Coord', type: 'operations', participantId: null, children: [] },
                { id: 'helicopter-coord', title: 'Operations - Helicopter Coord', type: 'operations', participantId: null, children: [] }
              ]
            }
          ]
        },
        {
          id: 'branch-1', title: 'Operations - Branch 1 Director', type: 'branch', participantId: null,
          children: [
            { id: 'div-1a', title: 'Operations - Division Group 1a Supervisor', type: 'division', participantId: null, children: [] },
            { id: 'div-1b', title: 'Operations - Division Group 1b Supervisor', type: 'division', participantId: null, children: [] },
            { id: 'div-1c', title: 'Operations - Division Group 1c Supervisor', type: 'division', participantId: null, children: [] },
            { id: 'div-1d', title: 'Operations - Division Group 1d Supervisor', type: 'division', participantId: null, children: [] }
          ]
        },
        {
          id: 'branch-2', title: 'Operations - Branch 2 Director', type: 'branch', participantId: null,
          children: [
            { id: 'div-2a', title: 'Operations - Division Group 2a Supervisor', type: 'division', participantId: null, children: [] },
            { id: 'div-2b', title: 'Operations - Division Group 2b Supervisor', type: 'division', participantId: null, children: [] },
            { id: 'div-2c', title: 'Operations - Division Group 2c Supervisor', type: 'division', participantId: null, children: [] },
            { id: 'div-2d', title: 'Operations - Division Group 2d Supervisor', type: 'division', participantId: null, children: [] }
          ]
        }
      ]
    },
    {
      id: 'planning', title: 'Planning - Planning Chief', type: 'planning', participantId: null,
      children: [
        { id: 'resources-unit', title: 'Planning - Resources Unit Leader', type: 'planning', participantId: null, children: [] },
        { id: 'situation-unit', title: 'Planning - Situation Leader', type: 'planning', participantId: null, children: [] },
        { id: 'documentation-unit', title: 'Planning - Documentation Unit Leader', type: 'planning', participantId: null, children: [] },
        { id: 'demob-unit', title: 'Planning - Demobilization Unit Leader', type: 'planning', participantId: null, children: [] },
        { id: 'tech-special', title: 'Planning - Tech Special', type: 'planning', participantId: null, children: [] }
      ]
    },
    {
      id: 'logistics', title: 'Logistics - Logistics Chief', type: 'logistics', participantId: null,
      children: [
        {
          id: 'support-branch', title: 'Logistics - Branch Support Director', type: 'branch', participantId: null,
          children: [
            { id: 'supply-unit', title: 'Logistics - Supply Unit Leader', type: 'logistics', participantId: null, children: [] },
            { id: 'facilities-unit', title: 'Logistics - Facilities Unit Leader', type: 'logistics', participantId: null, children: [] },
            { id: 'ground-support', title: 'Logistics - Ground Support Unit Leader', type: 'logistics', participantId: null, children: [] }
          ]
        },
        {
          id: 'service-branch', title: 'Logistics - Branch Services Director', type: 'branch', participantId: null,
          children: [
            { id: 'comms-unit', title: 'Logistics - Communications Unit Director', type: 'logistics', participantId: null, children: [] },
            { id: 'medical-unit', title: 'Logistics - Medical Unit Leader', type: 'logistics', participantId: null, children: [] },
            { id: 'food-unit', title: 'Logistics - Food Unit Leader', type: 'logistics', participantId: null, children: [] }
          ]
        }
      ]
    },
    {
      id: 'finance', title: 'Finance Chief', type: 'finance', participantId: null,
      children: [
        { id: 'time-unit', title: 'Finance/Admin - Time Unit Leader', type: 'finance', participantId: null, children: [] },
        { id: 'procurement-unit', title: 'Finance/Admin - Procurement Unit Leader', type: 'finance', participantId: null, children: [] },
        { id: 'compensation-unit', title: 'Finance/Admin - Compensation/Claims Unit Leader', type: 'finance', participantId: null, children: [] },
        { id: 'cost-unit', title: 'Finance/Admin - Cost Unit Leader', type: 'finance', participantId: null, children: [] }
      ]
    }
  ]
};

// Color schemes for different position types
const positionColors = {
  'incident-commander': {
    bg: 'bg-gradient-to-br from-orange-500/30 to-orange-600/20',
    border: 'border-orange-500',
    text: 'text-orange-400',
    label: 'Incident Commander'
  },
  'command-staff': {
    bg: 'bg-gradient-to-br from-red-500/30 to-red-600/20',
    border: 'border-red-500',
    text: 'text-red-400',
    label: 'Command Staff'
  },
  'operations': {
    bg: 'bg-gradient-to-br from-orange-400/30 to-amber-500/20',
    border: 'border-orange-400',
    text: 'text-orange-300',
    label: 'Operations'
  },
  'planning': {
    bg: 'bg-gradient-to-br from-blue-500/30 to-blue-600/20',
    border: 'border-blue-500',
    text: 'text-blue-400',
    label: 'Planning'
  },
  'logistics': {
    bg: 'bg-gradient-to-br from-amber-200/30 to-yellow-300/20',
    border: 'border-amber-300',
    text: 'text-amber-300',
    label: 'Logistics'
  },
  'finance': {
    bg: 'bg-gradient-to-br from-gray-400/30 to-gray-500/20',
    border: 'border-gray-400',
    text: 'text-gray-300',
    label: 'Finance/Admin'
  },
  'branch': {
    bg: 'bg-gradient-to-br from-purple-500/30 to-purple-600/20',
    border: 'border-purple-500',
    text: 'text-purple-400',
    label: 'Branch'
  },
  'division': {
    bg: 'bg-gradient-to-br from-pink-500/30 to-pink-600/20',
    border: 'border-pink-500',
    text: 'text-pink-400',
    label: 'Division'
  },
  'unit': {
    bg: 'bg-gradient-to-br from-cyan-500/30 to-cyan-600/20',
    border: 'border-cyan-500',
    text: 'text-cyan-400',
    label: 'Unit'
  },
  'task-force': {
    bg: 'bg-gradient-to-br from-emerald-500/30 to-emerald-600/20',
    border: 'border-emerald-500',
    text: 'text-emerald-400',
    label: 'Task Force'
  },
  'strike-team': {
    bg: 'bg-gradient-to-br from-rose-500/30 to-rose-600/20',
    border: 'border-rose-500',
    text: 'text-rose-400',
    label: 'Strike Team'
  }
};

const positionTypes = [
  { value: 'incident-commander', label: 'Incident Commander' },
  { value: 'command-staff', label: 'Command Staff' },
  { value: 'operations', label: 'Operations' },
  { value: 'planning', label: 'Planning' },
  { value: 'logistics', label: 'Logistics' },
  { value: 'finance', label: 'Finance/Admin' },
  { value: 'branch', label: 'Branch' },
  { value: 'division', label: 'Division' },
  { value: 'unit', label: 'Unit' },
  { value: 'task-force', label: 'Task Force' },
  { value: 'strike-team', label: 'Strike Team' }
];

// Helper function to match participant to position
// Handles cases like participant.position="Command - Incident Commander" matching title="Incident Commander"
// Roman <-> arabic numeral conversion (Branch 1 <-> Branch I, Division 2 <-> Division II)
const ROMAN_MAP = { i: '1', ii: '2', iii: '3', iv: '4', v: '5', vi: '6', vii: '7', viii: '8', ix: '9', x: '10' };
const romanToArabic = (str) => str.replace(/\b(x|ix|viii|vii|vi|v|iv|iii|ii|i)\b/g, (m) => ROMAN_MAP[m] || m);

// Extra token synonyms applied AFTER normalisation. Left side is what may
// appear in either participant.position or the org chart title; right side
// is the canonical form the matcher compares against.
const SYNONYM_REPLACEMENTS = [
  // "Operations Section Chief" == "Operations Chief"; strip the redundant "section" filler
  [/\bsection chief\b/g, 'chief'],
  // Finance / Admin: dropdown uses "Finance/Admin - ..." while the chart card
  // is titled just "Finance Chief". After separator-stripping the string
  // reads "finance admin ..."; collapse it to "finance ..." so exact match works.
  [/\bfinance\/admin\b/g, 'finance'],
  [/\bfinance admin\b/g, 'finance'],
  // Branch Support/Services (dropdown wording) == Support/Service Branch (ICS canonical)
  [/\bbranch support\b/g, 'support branch'],
  [/\bbranch services\b/g, 'service branch'],
  [/\bservices branch\b/g, 'service branch'],
  // Communications Unit is a Leader in ICS canon (dropdown mistakenly says Director)
  [/\bcommunications unit director\b/g, 'communications unit leader'],
  // Division Group / Group are filler prefixes to the letter/number tag
  [/\bdivision group\b/g, 'division'],
  // "Division 1a" ↔ "Division A"; strip leading digit clusters before the letter (1a, 2b …)
  [/\bdivision\s+\d+([a-z])\b/g, 'division $1'],
];

const normalizePositionStr = (str) => {
  let s = String(str || '').toLowerCase();
  // Drop parentheticals like "(deputy)" to allow substring matching
  s = s.replace(/\([^)]*\)/g, ' ');
  // Squash separators (dashes, slashes) and collapse whitespace
  s = s.replace(/[-/]+/g, ' ').replace(/\s+/g, ' ').trim();
  s = romanToArabic(s);
  for (const [re, repl] of SYNONYM_REPLACEMENTS) {
    s = s.replace(re, repl);
  }
  // Dedupe adjacent duplicate words: "planning planning chief" -> "planning chief"
  const tokens = s.split(' ').filter(Boolean);
  const deduped = [];
  for (const t of tokens) {
    if (deduped[deduped.length - 1] !== t) deduped.push(t);
  }
  return deduped.join(' ').trim();
};

const matchParticipantToPosition = (participants, position) => {
  // If a participant has been explicitly assigned to this position, ONLY that
  // participant matches. Falling through to title-based matching causes a bug
  // when multiple positions share the same title (e.g. two custom "EXRSIM"
  // positions) — every position would resolve to the first participant whose
  // `position` field matches the title, so the same name would show on every
  // card regardless of which participant was picked in "Assign Participant".
  if (position.participantId) {
    return participants.find(p => p.id === position.participantId);
  }

  // No explicit assignment — fall back to title-based auto-match.
  return participants.find(p => {
    if (!p.position || !position.title) return false;

    const pNorm = normalizePositionStr(p.position);
    const posNorm = normalizePositionStr(position.title);

    // Exact normalized match
    if (pNorm === posNorm) return true;

    // Participant position contains the org chart title
    if (pNorm.includes(posNorm)) return true;

    // Org chart title contains the participant position
    if (posNorm.includes(pNorm)) return true;

    // Check last segment match: "Planning - Demobilization Unit - Leader" → "Demobilization Unit Leader"
    const pParts = p.position.split(/\s*-\s*/).filter(s => s.trim());
    if (pParts.length > 1) {
      const lastParts = pParts.slice(1).join(' ').toLowerCase().trim();
      if (lastParts === posNorm || posNorm.includes(lastParts) || lastParts.includes(posNorm)) return true;
    }

    return false;
  });
};

// Position Card Component
const PositionCard = ({ 
  position, 
  participant, 
  onEdit, 
  onDelete, 
  onAddChild, 
  isRoot = false,
  hasChildren = false,
  isExpanded = false,
  onToggleExpand,
  readOnly = false,
  displayMode = false
}) => {
  const colors = positionColors[position.type] || positionColors['operations'];
  const isVacant = !participant;
  
  return (
    <div 
      className={`
        relative group rounded-xl text-center
        border-2 transition-all duration-300
        ${displayMode ? 'p-6 min-w-[260px] max-w-[320px]' : 'p-4 min-w-[180px] max-w-[220px] cursor-pointer'}
        ${colors.bg} ${colors.border}
        ${isVacant ? 'opacity-70 border-dashed' : ''}
        ${displayMode ? '' : 'hover:scale-105 hover:shadow-lg hover:shadow-' + colors.border + '/20'}
      `}
    >
      {/* Action Buttons - hidden in readOnly/display mode */}
      {!readOnly && (
      <div className="absolute -top-2 -right-2 hidden group-hover:flex gap-1 z-10">
        <button
          onClick={(e) => { e.stopPropagation(); onEdit(position); }}
          className="w-7 h-7 rounded-full bg-blue-500 text-white flex items-center justify-center hover:bg-blue-600 transition-colors shadow-lg"
          title="Edit Position"
        >
          <Edit className="w-3.5 h-3.5" />
        </button>
        {!isRoot && (
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(position.id); }}
            className="w-7 h-7 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600 transition-colors shadow-lg"
            title="Delete Position"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); onAddChild(position.id); }}
          className="w-7 h-7 rounded-full bg-green-500 text-white flex items-center justify-center hover:bg-green-600 transition-colors shadow-lg"
          title="Add Child Position"
        >
          <Plus className="w-3.5 h-3.5" />
        </button>
      </div>
      )}
      
      {/* Position Type Label */}
      <div className={`uppercase tracking-wider font-semibold mb-1 ${colors.text} ${displayMode ? 'text-xs' : 'text-[10px]'}`}>
        {colors.label}
      </div>
      
      {/* Position Title */}
      <div className={`font-bold text-white mb-2 leading-tight ${displayMode ? 'text-lg' : 'text-sm'}`}>
        {position.title}
      </div>
      
      {/* Participant Name */}
      <div className={`font-medium ${isVacant ? 'text-red-400 italic' : 'text-green-400'} ${displayMode ? 'text-base' : 'text-xs'}`}>
        {participant 
          ? `${participant.lastName || participant.lastname || ''}, ${participant.firstName || participant.firstname || ''}`.trim() || participant.name
          : 'Vacant'
        }
      </div>
      
      {/* Expand/Collapse Button */}
      {hasChildren && (
        <button
          onClick={(e) => { e.stopPropagation(); onToggleExpand(); }}
          className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 w-6 h-6 rounded-full bg-gray-700 border border-gray-600 flex items-center justify-center hover:bg-gray-600 transition-colors z-10"
        >
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-gray-300" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-300" />
          )}
        </button>
      )}
    </div>
  );
};

// Tree Node Component with connecting lines
const TreeNode = ({ 
  position, 
  exerciseParticipants,
  onEdit, 
  onDelete, 
  onAddChild,
  expandedNodes,
  toggleNode,
  readOnly = false,
  displayMode = false
}) => {
  const isExpanded = expandedNodes[position.id] || false;
  
  // Find participant assigned to this position using enhanced matching
  const participant = matchParticipantToPosition(exerciseParticipants, position);
  
  const hasChildren = position.children && position.children.length > 0;
  
  return (
    <div className="flex flex-col items-center">
      {/* Position Card */}
      <PositionCard
        position={position}
        participant={participant}
        onEdit={onEdit}
        onDelete={onDelete}
        onAddChild={onAddChild}
        hasChildren={hasChildren}
        isExpanded={isExpanded}
        onToggleExpand={() => toggleNode(position.id)}
        readOnly={readOnly}
        displayMode={displayMode}
      />
      
      {/* Children */}
      {hasChildren && isExpanded && (
        <>
          <div className="w-0.5 h-6 bg-gray-600 mt-3"></div>
          
          {/* Horizontal Connector */}
          {position.children.length > 1 && (
            <div 
              className="h-0.5 bg-gray-600"
              style={{ 
                width: `${Math.min(position.children.length * 200, 800)}px`,
                maxWidth: '90vw'
              }}
            ></div>
          )}
          
          {/* Children */}
          <div className="flex flex-wrap justify-center gap-4 mt-2">
            {position.children.map((child) => (
              <div key={child.id} className="flex flex-col items-center">
                {position.children.length > 1 && (
                  <div className="w-0.5 h-4 bg-gray-600"></div>
                )}
                <TreeNode
                  position={child}
                  exerciseParticipants={exerciseParticipants}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onAddChild={onAddChild}
                  expandedNodes={expandedNodes}
                  toggleNode={toggleNode}
                  readOnly={readOnly}
                  displayMode={displayMode}
                />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// Main Component
const ICSOrganizationChart = ({ participants = [], theme, positions = [], currentExercise, API_URL, displayMode = false }) => {
  const [orgStructure, setOrgStructure] = useState(defaultOrgStructure);
  const [expandedNodes, setExpandedNodes] = useState({}); // All collapsed by default
  const [editingPosition, setEditingPosition] = useState(null);
  const [addingToParentId, setAddingToParentId] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [fetchedParticipants, setFetchedParticipants] = useState([]);
  
  // Form state for editing
  const [formData, setFormData] = useState({
    title: '',
    type: 'operations',
    participantId: ''
  });
  
  const [hasAutoPopulated, setHasAutoPopulated] = useState(false);

  // Use fetched participants if available, otherwise filter from props
  const exerciseParticipants = fetchedParticipants.length > 0
    ? fetchedParticipants
    : participants.filter(p => 
        p.assigned_exercise === currentExercise?.id || 
        p.assigned_exercise === currentExercise?.exercise_name
      );

  // Auto-populate helper: assign participants to matching positions
  const autoPopulateStructure = (structure, participantsList) => {
    if (!participantsList || participantsList.length === 0) return structure;

    // Use the same shared normalizer as the render-time matcher so both paths
    // treat "Planning - Section Chief" == "Planning Chief", etc.
    const normalizeForMatch = normalizePositionStr;

    // Collect already-assigned participantIds
    const collectAssigned = (node) => {
      const ids = new Set();
      if (node.participantId) ids.add(node.participantId);
      (node.children || []).forEach(c => collectAssigned(c).forEach(id => ids.add(id)));
      return ids;
    };
    const assignedIds = new Set();
    if (structure.participantId) assignedIds.add(structure.participantId);
    (structure.commandStaff || []).forEach(n => collectAssigned(n).forEach(id => assignedIds.add(id)));
    (structure.generalStaff || []).forEach(n => collectAssigned(n).forEach(id => assignedIds.add(id)));

    const findMatch = (positionTitle) => {
      if (!positionTitle) return null;
      const titleNorm = normalizeForMatch(positionTitle);
      return participantsList.find(p => {
        if (!p.position || assignedIds.has(p.id)) return false;
        const pNorm = normalizeForMatch(p.position);
        if (pNorm === titleNorm) return true;
        if (pNorm.includes(titleNorm)) return true;
        if (titleNorm.includes(pNorm)) return true;
        const pParts = p.position.split(/\s*-\s*/).filter(s => s.trim());
        if (pParts.length > 1) {
          const lastParts = pParts.slice(1).join(' ').toLowerCase().trim();
          if (lastParts === titleNorm || titleNorm.includes(lastParts) || lastParts.includes(titleNorm)) return true;
        }
        return false;
      });
    };

    const populateChildren = (children) => {
      let changed = false;
      const updated = children.map(node => {
        let newNode = { ...node };
        if (!newNode.participantId) {
          const match = findMatch(newNode.title);
          if (match) {
            newNode.participantId = match.id;
            assignedIds.add(match.id);
            changed = true;
          }
        }
        if (newNode.children && newNode.children.length > 0) {
          const result = populateChildren(newNode.children);
          if (result.changed) {
            newNode = { ...newNode, children: result.children };
            changed = true;
          }
        }
        return newNode;
      });
      return { children: updated, changed };
    };

    let newStructure = { ...structure };
    let structureChanged = false;

    if (!newStructure.participantId) {
      const icMatch = findMatch(newStructure.title);
      if (icMatch) {
        newStructure.participantId = icMatch.id;
        assignedIds.add(icMatch.id);
        structureChanged = true;
      }
    }
    if (newStructure.commandStaff?.length > 0) {
      const result = populateChildren(newStructure.commandStaff);
      if (result.changed) {
        newStructure = { ...newStructure, commandStaff: result.children };
        structureChanged = true;
      }
    }
    if (newStructure.generalStaff?.length > 0) {
      const result = populateChildren(newStructure.generalStaff);
      if (result.changed) {
        newStructure = { ...newStructure, generalStaff: result.children };
        structureChanged = true;
      }
    }

    return { structure: newStructure, changed: structureChanged };
  };
  
  // Extract unique positions from exercise participants for Position Type dropdown
  const participantPositionTypes = React.useMemo(() => {
    const uniquePositions = [...new Set(
      exerciseParticipants
        .map(p => p.position)
        .filter(pos => pos && pos.trim())
    )].sort();
    
    // Combine with default position types, participant positions first
    const participantOptions = uniquePositions.map(pos => ({
      value: pos.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''),
      label: pos,
      isParticipantPosition: true,
      originalPosition: pos
    }));
    
    // Add separator and default types
    const defaultOptions = positionTypes.map(type => ({
      ...type,
      isParticipantPosition: false,
      originalPosition: null
    }));
    
    return { participantOptions, defaultOptions, allOptions: [...participantOptions, ...defaultOptions] };
  }, [exerciseParticipants]);
  
  // Find participants matching a position type
  const getParticipantsByPosition = (positionLabel) => {
    return exerciseParticipants.filter(p => 
      p.position && p.position.toLowerCase() === positionLabel.toLowerCase()
    );
  };
  
  // Handle Position Type change with auto-populate
  const handlePositionTypeChange = (value, isEdit = false) => {
    // Find the selected option to get the original position label
    const selectedOption = participantPositionTypes.allOptions.find(opt => opt.value === value);
    
    let newFormData = { ...formData, type: value };
    
    // If this is a participant position, check for matching participants
    if (selectedOption?.isParticipantPosition && selectedOption?.originalPosition) {
      const matchingParticipants = getParticipantsByPosition(selectedOption.originalPosition);
      
      // Auto-populate if there's exactly one matching participant
      if (matchingParticipants.length === 1) {
        newFormData.participantId = matchingParticipants[0].id;
        toast.success(`Auto-assigned: ${matchingParticipants[0].lastName || ''}, ${matchingParticipants[0].firstName || ''}`);
      } else if (matchingParticipants.length > 1) {
        toast.info(`${matchingParticipants.length} participants match this position. Please select one.`);
      }
    }
    
    setFormData(newFormData);
  };
  
  // Load saved organization structure AND participants, then auto-populate
  const [refreshTick, setRefreshTick] = useState(0);
  useEffect(() => {
    // Reset auto-populate flag when exercise changes
    setHasAutoPopulated(false);
    
    const loadOrgStructureAndPopulate = async () => {
      if (!currentExercise?.id || !API_URL) {
        setIsLoading(false);
        return;
      }
      
      try {
        const token = secureStorage.getToken();
        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        };
        
        // Fetch both org structure and participants in parallel
        const [orgResponse, participantsResponse] = await Promise.all([
          fetch(`${API_URL}/api/exercise-builder/${currentExercise.id}/org-structure`, { headers }),
          fetch(`${API_URL}/api/exercises/${currentExercise.id}/participants`, { headers })
        ]);
        
        // Parse org structure
        let structure = defaultOrgStructure;
        if (orgResponse.ok) {
          const data = await orgResponse.json();
          if (data.organizational_structure) {
            try {
              // One-time on-load migration: legacy exercises stored the long form
              // "Air Operations Branch Director". Rename it to the current short form
              // so cards render with the canonical title.
              const migrated = data.organizational_structure.replace(
                /Air Operations Branch Director/g,
                'Air Ops Branch Director'
              );
              structure = JSON.parse(migrated);
            } catch (parseError) {
              console.warn('Failed to parse saved org structure, using default');
            }
          }
        }

        // Backfill: ensure Finance/Admin Chief exists in General Staff for
        // older exercises whose saved structure predates that section.
        try {
          const gs = Array.isArray(structure.generalStaff) ? structure.generalStaff : [];
          const hasFinance = gs.some((n) => n?.id === 'finance' || n?.type === 'finance');
          if (!hasFinance) {
            const financeDefault = (defaultOrgStructure.generalStaff || []).find((n) => n.id === 'finance');
            if (financeDefault) {
              structure = { ...structure, generalStaff: [...gs, financeDefault] };
            }
          }
        } catch (backfillErr) {
          console.warn('General Staff backfill skipped:', backfillErr);
        }
        
        // Parse participants
        let exerciseParticipantsList = [];
        if (participantsResponse.ok) {
          exerciseParticipantsList = await participantsResponse.json();
          setFetchedParticipants(exerciseParticipantsList);
        }
        
        // Auto-populate: match participants to positions
        if (exerciseParticipantsList.length > 0) {
          const result = autoPopulateStructure(structure, exerciseParticipantsList);
          if (result.changed) {
            structure = result.structure;
            setHasAutoPopulated(true);
          }
        }
        
        setOrgStructure(structure);
        // Default display: expand only the root Incident Commander so both
        // Command Staff and General Staff rows are visible on first load.
        // Chiefs remain collapsed until the user clicks them.
        if (!displayMode) {
          setExpandedNodes((prev) => ({ ...prev, [structure.id]: true }));
        }
      } catch (error) {
        console.error('Error loading org structure:', error);
        setOrgStructure(defaultOrgStructure);
      } finally {
        setIsLoading(false);
        setHasLoadedOnce(true);
      }
    };
    
    loadOrgStructureAndPopulate();
  }, [currentExercise?.id, API_URL, refreshTick]);

  // Refetch participants + re-run auto-populate on demand
  const refreshFromParticipants = () => {
    setRefreshTick((t) => t + 1);
  };
  
  // Save organization structure to backend (debounced)
  // Save organization structure to backend (debounced)
  const saveOrgStructure = useCallback(async (structure) => {
    if (!currentExercise?.id || !API_URL || !hasLoadedOnce) return;
    
    setIsSaving(true);
    try {
      const token = secureStorage.getToken();
      const response = await fetch(`${API_URL}/api/exercise-builder/${currentExercise.id}/org-structure`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          organizational_structure: JSON.stringify(structure)
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to save org structure');
      }
    } catch (error) {
      console.error('Error saving org structure:', error);
      toast.error('Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  }, [currentExercise?.id, API_URL, hasLoadedOnce]);
  
  // Debounced save effect - save when orgStructure changes
  useEffect(() => {
    if (!hasLoadedOnce) return;
    
    const timeoutId = setTimeout(() => {
      saveOrgStructure(orgStructure);
    }, 500); // 500ms debounce
    
    return () => clearTimeout(timeoutId);
  }, [orgStructure, hasLoadedOnce, saveOrgStructure]);
  
  // Toggle node expansion
  const toggleNode = (nodeId) => {
    setExpandedNodes(prev => ({
      ...prev,
      [nodeId]: !prev[nodeId]
    }));
  };
  
  // Auto-expand everything after load when in display mode
  useEffect(() => {
    if (!displayMode || isLoading) return;
    const getAllNodeIds = (nodes) => {
      let ids = {};
      (nodes || []).forEach(node => {
        ids[node.id] = true;
        if (node.children && node.children.length > 0) {
          ids = { ...ids, ...getAllNodeIds(node.children) };
        }
      });
      return ids;
    };
    let allIds = { [orgStructure.id]: true };
    allIds = { ...allIds, ...getAllNodeIds(orgStructure.commandStaff), ...getAllNodeIds(orgStructure.generalStaff) };
    setExpandedNodes(allIds);
  }, [displayMode, isLoading, orgStructure]);

  // Expand all nodes
  const expandAll = () => {
    const getAllNodeIds = (nodes) => {
      let ids = {};
      nodes.forEach(node => {
        ids[node.id] = true;
        if (node.children && node.children.length > 0) {
          ids = { ...ids, ...getAllNodeIds(node.children) };
        }
      });
      return ids;
    };
    
    let allIds = { [orgStructure.id]: true };
    if (orgStructure.commandStaff) {
      allIds = { ...allIds, ...getAllNodeIds(orgStructure.commandStaff) };
    }
    if (orgStructure.generalStaff) {
      allIds = { ...allIds, ...getAllNodeIds(orgStructure.generalStaff) };
    }
    setExpandedNodes(allIds);
  };
  
  // Collapse all nodes
  const collapseAll = () => {
    setExpandedNodes({});
  };
  
  // Find position in tree by ID
  const findPositionById = (id) => {
    // Check IC
    if (orgStructure.id === id) return orgStructure;
    
    // Check command staff
    const findInArray = (arr, targetId) => {
      for (const item of arr) {
        if (item.id === targetId) return item;
        if (item.children) {
          const found = findInArray(item.children, targetId);
          if (found) return found;
        }
      }
      return null;
    };
    
    let found = findInArray(orgStructure.commandStaff || [], id);
    if (found) return found;
    
    found = findInArray(orgStructure.generalStaff || [], id);
    return found;
  };
  
  // Update position in tree
  const updatePositionInTree = (arr, id, updates) => {
    return arr.map(item => {
      if (item.id === id) {
        return { ...item, ...updates };
      }
      if (item.children) {
        return { ...item, children: updatePositionInTree(item.children, id, updates) };
      }
      return item;
    });
  };
  
  // Add child to position
  const addChildToPosition = (arr, parentId, newChild) => {
    return arr.map(item => {
      if (item.id === parentId) {
        return { ...item, children: [...(item.children || []), newChild] };
      }
      if (item.children) {
        return { ...item, children: addChildToPosition(item.children, parentId, newChild) };
      }
      return item;
    });
  };
  
  // Delete position from tree
  const deletePositionFromTree = (arr, id) => {
    return arr
      .filter(item => item.id !== id)
      .map(item => {
        if (item.children) {
          return { ...item, children: deletePositionFromTree(item.children, id) };
        }
        return item;
      });
  };
  
  // Handle edit position
  // PATCH the newly-assigned participant's position on the master Participants
  // record so the two views stay in sync. Fire-and-forget; the local ICS 203
  // state has already been updated, so a failure only breaks the reverse sync.
  const syncParticipantPosition = async (participantId, newTitle, previousParticipantId = null) => {
    if (!participantId || participantId === previousParticipantId) return;
    try {
      const token = secureStorage.getToken();
      await axios.patch(
        `${API_URL}/api/participants/${participantId}/position`,
        { position: newTitle },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Reflect the change immediately in the local participants list so cards
      // rerender with the new position label without waiting for a refetch.
      setFetchedParticipants((prev) =>
        prev.map((p) => (p.id === participantId ? { ...p, position: newTitle } : p))
      );
    } catch (err) {
      console.error('Failed to sync participant position:', err);
      toast.error("Position updated on chart, but couldn't update the participant record.");
    }
  };

  const handleEdit = (position) => {
    setEditingPosition(position);
    setFormData({
      title: position.title,
      type: position.type,
      participantId: position.participantId || ''
    });
    setShowEditModal(true);
  };
  
  // Handle save edit
  const handleSaveEdit = () => {
    if (!formData.title.trim()) {
      toast.error('Position title is required');
      return;
    }
    
    const previousParticipantId = editingPosition?.participantId || null;
    const updates = {
      title: formData.title,
      type: formData.type,
      participantId: formData.participantId || null
    };
    
    // Update IC if that's what we're editing
    if (editingPosition.id === orgStructure.id) {
      setOrgStructure(prev => ({ ...prev, ...updates }));
    } else {
      setOrgStructure(prev => ({
        ...prev,
        commandStaff: updatePositionInTree(prev.commandStaff || [], editingPosition.id, updates),
        generalStaff: updatePositionInTree(prev.generalStaff || [], editingPosition.id, updates)
      }));
    }
    
    // Push the position title back onto the newly-assigned participant so the
    // Participants master list stays in sync with the ICS 203 chart.
    if (updates.participantId) {
      syncParticipantPosition(updates.participantId, updates.title, previousParticipantId);
    }

    setShowEditModal(false);
    setEditingPosition(null);
    toast.success('Position updated successfully');
  };
  
  // Handle add child
  const handleAddChild = (parentId) => {
    setAddingToParentId(parentId);
    setFormData({
      title: '',
      type: 'operations',
      participantId: ''
    });
    setShowAddModal(true);
  };
  
  // Handle save new position
  const handleSaveNew = () => {
    if (!formData.title.trim()) {
      toast.error('Position title is required');
      return;
    }
    
    const newPosition = {
      id: `pos-${Date.now()}`,
      title: formData.title,
      type: formData.type,
      participantId: formData.participantId || null,
      children: []
    };
    
    // Determine where to add based on parent
    if (addingToParentId === orgStructure.id) {
      // Adding to IC - determine if command or general staff based on type
      if (formData.type === 'command-staff') {
        setOrgStructure(prev => ({
          ...prev,
          commandStaff: [...(prev.commandStaff || []), newPosition]
        }));
      } else {
        setOrgStructure(prev => ({
          ...prev,
          generalStaff: [...(prev.generalStaff || []), newPosition]
        }));
      }
    } else {
      setOrgStructure(prev => ({
        ...prev,
        commandStaff: addChildToPosition(prev.commandStaff || [], addingToParentId, newPosition),
        generalStaff: addChildToPosition(prev.generalStaff || [], addingToParentId, newPosition)
      }));
    }
    
    // Auto-expand parent
    setExpandedNodes(prev => ({ ...prev, [addingToParentId]: true }));

    // Sync newly-assigned participant's position on the Participants list
    if (newPosition.participantId) {
      syncParticipantPosition(newPosition.participantId, newPosition.title);
    }

    setShowAddModal(false);
    setAddingToParentId(null);
    toast.success('Position added successfully');
  };
  
  // Handle delete position
  const handleDelete = (positionId) => {
    setShowDeleteConfirm(positionId);
  };
  
  // Confirm delete
  const confirmDelete = () => {
    setOrgStructure(prev => ({
      ...prev,
      commandStaff: deletePositionFromTree(prev.commandStaff || [], showDeleteConfirm),
      generalStaff: deletePositionFromTree(prev.generalStaff || [], showDeleteConfirm)
    }));
    setShowDeleteConfirm(null);
    toast.success('Position deleted successfully');
  };
  
  // Reset to default
  const handleReset = () => {
    setOrgStructure(defaultOrgStructure);
    setExpandedNodes({});
    saveOrgStructure(defaultOrgStructure);
    toast.success('Organization chart reset to CICS default');
  };
  
  // Count positions
  const countPositions = (arr) => {
    let count = 0;
    arr.forEach(item => {
      count += 1;
      if (item.children) {
        count += countPositions(item.children);
      }
    });
    return count;
  };
  
  // Count filled positions
  const countFilledPositions = (arr) => {
    let count = 0;
    arr.forEach(item => {
      if (matchParticipantToPosition(exerciseParticipants, item)) {
        count += 1;
      }
      if (item.children) {
        count += countFilledPositions(item.children);
      }
    });
    return count;
  };
  
  const totalPositions = 1 + countPositions(orgStructure.commandStaff || []) + countPositions(orgStructure.generalStaff || []);
  const filledIC = matchParticipantToPosition(exerciseParticipants, orgStructure) ? 1 : 0;
  const filledPositions = filledIC + countFilledPositions(orgStructure.commandStaff || []) + countFilledPositions(orgStructure.generalStaff || []);
  
  // Find participant for IC
  const icParticipant = matchParticipantToPosition(exerciseParticipants, orgStructure);
  
  // Print handler
  const handlePrint = () => {
    const printContent = generatePrintContent();
    const printWindow = window.open('', '_blank');
    writeSanitizedHTML(printWindow.document, printContent);
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };
  
  const generatePrintContent = () => {
    const generatePositionHTML = (node, level = 0) => {
      const colors = positionColors[node.type] || positionColors['operations'];
      const participant = matchParticipantToPosition(exerciseParticipants, node);
      const participantName = participant 
        ? `${participant.lastName || participant.lastname || ''}, ${participant.firstName || participant.firstname || ''}`.trim()
        : 'Vacant';
      
      const bgColors = {
        'incident-commander': '#fed7aa',
        'command-staff': '#fecaca',
        'operations': '#ffedd5',
        'planning': '#dbeafe',
        'logistics': '#fef3c7',
        'finance': '#e5e7eb',
        'branch': '#e9d5ff',
        'division': '#fbcfe8',
        'unit': '#cffafe',
        'task-force': '#d1fae5',
        'strike-team': '#ffe4e6'
      };
      
      let html = `
        <div style="margin-left: ${level * 20}px; margin-bottom: 8px;">
          <div style="background: ${bgColors[node.type] || '#f3f4f6'}; border: 1px solid #ddd; border-radius: 8px; padding: 10px; display: inline-block; min-width: 200px;">
            <div style="font-size: 10px; color: #666; text-transform: uppercase;">${colors.label}</div>
            <div style="font-weight: bold; margin: 4px 0;">${node.title}</div>
            <div style="font-size: 12px; color: ${participant ? '#16a34a' : '#dc2626'};">${participantName}</div>
          </div>
        </div>
      `;
      
      if (node.children && node.children.length > 0) {
        node.children.forEach(child => {
          html += generatePositionHTML(child, level + 1);
        });
      }
      
      return html;
    };
    
    let orgHtml = generatePositionHTML(orgStructure, 0);
    
    // Add Command Staff
    orgHtml += '<h3 style="margin: 20px 0 10px 20px; color: #dc2626;">Command Staff</h3>';
    (orgStructure.commandStaff || []).forEach(pos => {
      orgHtml += generatePositionHTML(pos, 1);
    });
    
    // Add General Staff
    orgHtml += '<h3 style="margin: 20px 0 10px 20px; color: #f97316;">General Staff (Chiefs)</h3>';
    (orgStructure.generalStaff || []).forEach(pos => {
      orgHtml += generatePositionHTML(pos, 1);
    });
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>ICS 203 - Organization Assignment List</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          .header { text-align: center; margin-bottom: 30px; border-bottom: 3px solid #f97316; padding-bottom: 15px; }
          .header h1 { color: #f97316; margin-bottom: 5px; }
          .stats { display: flex; justify-content: center; gap: 40px; margin: 20px 0; }
          .stat { text-align: center; }
          .stat-value { font-size: 24px; font-weight: bold; color: #f97316; }
          .stat-label { font-size: 12px; color: #666; }
          .legend { display: flex; flex-wrap: wrap; justify-content: center; gap: 15px; margin: 20px 0; padding: 15px; background: #f9fafb; border-radius: 8px; }
          .legend-item { display: flex; align-items: center; gap: 5px; font-size: 11px; }
          .legend-color { width: 16px; height: 16px; border-radius: 4px; }
          .footer { margin-top: 40px; text-align: center; font-size: 12px; color: #666; border-top: 2px solid #ddd; padding-top: 15px; }
          @media print { body { margin: 10px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>ICS 203 - Organization Assignment List</h1>
          <p>Canadian Incident Command System</p>
          <p><strong>Exercise:</strong> ${currentExercise?.exercise_name || 'N/A'}</p>
          <p>Generated: ${new Date().toLocaleString()}</p>
        </div>
        
        <div class="stats">
          <div class="stat">
            <div class="stat-value">${totalPositions}</div>
            <div class="stat-label">Total Positions</div>
          </div>
          <div class="stat">
            <div class="stat-value">${filledPositions}</div>
            <div class="stat-label">Filled Positions</div>
          </div>
          <div class="stat">
            <div class="stat-value">${totalPositions - filledPositions}</div>
            <div class="stat-label">Vacant Positions</div>
          </div>
          <div class="stat">
            <div class="stat-value">${totalPositions > 0 ? ((filledPositions / totalPositions) * 100).toFixed(0) : 0}%</div>
            <div class="stat-label">Fill Rate</div>
          </div>
        </div>
        
        <div class="legend">
          <div class="legend-item"><div class="legend-color" style="background: #fecaca;"></div> Command Staff</div>
          <div class="legend-item"><div class="legend-color" style="background: #ffedd5;"></div> Operations</div>
          <div class="legend-item"><div class="legend-color" style="background: #dbeafe;"></div> Planning</div>
          <div class="legend-item"><div class="legend-color" style="background: #fef3c7;"></div> Logistics</div>
          <div class="legend-item"><div class="legend-color" style="background: #e5e7eb;"></div> Finance/Admin</div>
          <div class="legend-item"><div class="legend-color" style="background: #e9d5ff;"></div> Branch</div>
          <div class="legend-item"><div class="legend-color" style="background: #fbcfe8;"></div> Division</div>
          <div class="legend-item"><div class="legend-color" style="background: #cffafe;"></div> Unit</div>
          <div class="legend-item"><div class="legend-color" style="background: #d1fae5;"></div> Task Force</div>
          <div class="legend-item"><div class="legend-color" style="background: #ffe4e6;"></div> Strike Team</div>
        </div>
        
        <div class="org-structure">
          ${orgHtml}
        </div>
        
        <div class="footer">
          <p><strong>ICS 203 - Organization Assignment List</strong></p>
          <p>Powered by EXRSIM - Emergency Response Simulation Platform</p>
        </div>
      </body>
      </html>
    `;
  };
  
  return (
    <div className={`${displayMode ? 'p-4 bg-black min-h-screen' : 'p-4'} space-y-6 h-full flex flex-col`}>
      {/* Header - Fixed (hidden in display mode) */}
      {!displayMode && (
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 flex-shrink-0">
        <div>
          <h1 className="text-2xl font-bold text-orange-500 flex items-center gap-2">
            ICS 203 - Organization List
            {isSaving && (
              <span className="text-sm font-normal text-gray-400 animate-pulse">Saving...</span>
            )}
          </h1>
          <p className="text-gray-400 text-sm mt-1">
            Canadian Incident Command System - Interactive Organization Chart
          </p>
        </div>
        
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={expandAll}
            variant="outline"
            className="border-gray-600 text-gray-300 hover:bg-gray-800"
            disabled={isLoading}
          >
            <ChevronDown className="w-4 h-4 mr-2" />
            Expand All
          </Button>
          <Button
            onClick={collapseAll}
            variant="outline"
            className="border-gray-600 text-gray-300 hover:bg-gray-800"
            disabled={isLoading}
          >
            <ChevronRight className="w-4 h-4 mr-2" />
            Collapse All
          </Button>
          <Button
            onClick={refreshFromParticipants}
            variant="outline"
            className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
            disabled={isLoading}
            title="Refetch participants and re-match assignments"
            data-testid="ics203-refresh-btn"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button
            onClick={handleReset}
            variant="outline"
            className="border-gray-600 text-gray-300 hover:bg-gray-800"
            disabled={isLoading}
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Reset
          </Button>
          <Button
            onClick={() => {
              const url = currentExercise?.id ? `#display-ics203?exercise=${currentExercise.id}` : '#display-ics203';
              window.open(url, '_blank');
            }}
            variant="outline"
            className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
            disabled={isLoading || !currentExercise?.id}
            title="Open in Display Mode"
            data-testid="ics203-display-mode-btn"
          >
            <Monitor className="w-4 h-4 mr-2" />
            Display Mode
          </Button>
          <Button
            onClick={handlePrint}
            className="bg-orange-500 hover:bg-orange-600 text-black"
            disabled={isLoading}
          >
            <Printer className="w-4 h-4 mr-2" />
            Print ICS 203
          </Button>
        </div>
      </div>
      )}
      
      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500 mx-auto mb-4"></div>
            <p className="text-gray-400">Loading organization structure...</p>
          </div>
        </div>
      )}
      
      {!isLoading && (
        <>
      
      {/* Legend - Fixed (hidden in display mode) */}
      {!displayMode && (
      <Card className="bg-gray-800/50 border-gray-700 flex-shrink-0">
        <CardContent className="p-4">
          <div className="flex flex-wrap justify-center gap-4">
            {Object.entries(positionColors).map(([key, value]) => (
              <div key={key} className="flex items-center gap-2">
                <div className={`w-4 h-4 rounded ${value.bg} ${value.border} border`}></div>
                <span className="text-xs text-gray-400">{value.label}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
      )}
      
      {/* Organization Chart - Scrollable Container */}
      <Card className={`${displayMode ? 'bg-black border-black' : 'bg-gray-900/50 border-gray-700'} flex-1 min-h-0`}>
        <CardContent className="p-0 h-full">
          <div 
            className={`org-chart-scroll overflow-auto h-full ${displayMode ? 'max-h-[calc(100vh-40px)] p-4' : 'max-h-[calc(100vh-280px)] p-8'}`}
            style={{ 
              minHeight: '500px',
              scrollbarWidth: 'thin',
              scrollbarColor: '#4b5563 #1f2937'
            }}
          >
            <div className={displayMode ? 'min-w-[1400px] pb-12' : 'min-w-[1000px] pb-12'}>
              <div className="flex flex-col items-center">
            {/* Incident Commander */}
            <PositionCard
              position={orgStructure}
              participant={icParticipant}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onAddChild={handleAddChild}
              isRoot={true}
              hasChildren={true}
              isExpanded={expandedNodes[orgStructure.id]}
              onToggleExpand={() => toggleNode(orgStructure.id)}
              readOnly={displayMode}
              displayMode={displayMode}
            />
            
            {expandedNodes[orgStructure.id] && (
              <>
                {/* Vertical line from IC */}
                <div className="w-0.5 h-8 bg-gray-600 mt-3"></div>
                
                {/* Command Staff Row */}
                {orgStructure.commandStaff && orgStructure.commandStaff.length > 0 && (
                  <>
                    <div className="text-xs text-red-400 font-semibold uppercase tracking-wider mb-2">Command Staff</div>
                    <div 
                      className="h-0.5 bg-gray-600 mb-2"
                      style={{ width: `${Math.min(orgStructure.commandStaff.length * 200, 600)}px` }}
                    ></div>
                    <div className="flex flex-wrap justify-center gap-4 mb-6">
                      {orgStructure.commandStaff.map((pos) => (
                        <div key={pos.id} className="flex flex-col items-center">
                          <div className="w-0.5 h-4 bg-gray-600"></div>
                          <TreeNode
                            position={pos}
                            exerciseParticipants={exerciseParticipants}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                            onAddChild={handleAddChild}
                            expandedNodes={expandedNodes}
                            toggleNode={toggleNode}
                            readOnly={displayMode}
                            displayMode={displayMode}
                          />
                        </div>
                      ))}
                    </div>
                  </>
                )}
                
                {/* Vertical line to General Staff */}
                <div className="w-0.5 h-8 bg-gray-600"></div>
                
                {/* General Staff Row (Chiefs) */}
                {orgStructure.generalStaff && orgStructure.generalStaff.length > 0 && (
                  <>
                    <div className="text-xs text-orange-400 font-semibold uppercase tracking-wider mb-2">General Staff</div>
                    <div 
                      className="h-0.5 bg-gray-600 mb-2"
                      style={{ width: `${Math.min(orgStructure.generalStaff.length * 220, 1200)}px` }}
                    ></div>
                    <div className="flex flex-nowrap justify-center gap-4 overflow-x-auto pb-2 max-w-full">
                      {orgStructure.generalStaff.map((pos) => (
                        <div key={pos.id} className="flex flex-col items-center flex-shrink-0">
                          <div className="w-0.5 h-4 bg-gray-600"></div>
                          <TreeNode
                            position={pos}
                            exerciseParticipants={exerciseParticipants}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                            onAddChild={handleAddChild}
                            expandedNodes={expandedNodes}
                            toggleNode={toggleNode}
                            readOnly={displayMode}
                            displayMode={displayMode}
                          />
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </>
            )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <Card className="bg-gray-800 border-gray-700 w-full max-w-md mx-4 max-h-[85vh] flex flex-col">
            <CardHeader className="flex-shrink-0">
              <CardTitle className="text-orange-500 flex items-center justify-between">
                Edit Position
                <button onClick={() => setShowEditModal(false)} className="text-gray-400 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 overflow-y-auto flex-1">
              <div>
                <Label className="text-white">Position Title</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="bg-gray-900 border-gray-700 text-white"
                  placeholder="Enter position title"
                />
              </div>
              
              <div>
                <Label className="text-white">Position Type</Label>
                <Select 
                  value={formData.type} 
                  onValueChange={(value) => handlePositionTypeChange(value, true)}
                >
                  <SelectTrigger className="bg-gray-900 border-gray-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700 max-h-[300px]">
                    {participantPositionTypes.participantOptions.length > 0 && (
                      <>
                        <div className="px-2 py-1.5 text-xs font-semibold text-orange-400 bg-gray-900">
                          From Assigned Participants
                        </div>
                        {participantPositionTypes.participantOptions.map(type => (
                          <SelectItem key={type.value} value={type.value} className="text-white">
                            {type.label}
                          </SelectItem>
                        ))}
                        <div className="my-1 border-t border-gray-600" />
                        <div className="px-2 py-1.5 text-xs font-semibold text-gray-400 bg-gray-900">
                          Standard ICS Positions
                        </div>
                      </>
                    )}
                    {participantPositionTypes.defaultOptions.map(type => (
                      <SelectItem key={type.value} value={type.value} className="text-white">
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-white">Assign Participant</Label>
                <Select 
                  value={formData.participantId || "none"} 
                  onValueChange={(value) => setFormData({ ...formData, participantId: value === "none" ? "" : value })}
                >
                  <SelectTrigger className="bg-gray-900 border-gray-700 text-white">
                    <SelectValue placeholder="Select participant..." />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700">
                    <SelectItem value="none" className="text-gray-400">-- Leave Vacant --</SelectItem>
                    {exerciseParticipants.map(p => (
                      <SelectItem key={p.id} value={p.id} className="text-white">
                        {`${p.lastName || p.lastname || ''}, ${p.firstName || p.firstname || ''}`.trim() || p.name} {p.position ? `(${p.position})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-gray-500 mt-1">
                  Assigning a participant here also updates their <b>Position</b> in the Participants list to match this card.
                </p>
              </div>
              
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowEditModal(false)} className="border-gray-600">
                  Cancel
                </Button>
                <Button onClick={handleSaveEdit} className="bg-orange-500 hover:bg-orange-600 text-black">
                  Save Changes
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <Card className="bg-gray-800 border-gray-700 w-full max-w-md mx-4 max-h-[85vh] flex flex-col">
            <CardHeader className="flex-shrink-0">
              <CardTitle className="text-green-500 flex items-center justify-between">
                Add New Position
                <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 overflow-y-auto flex-1">
              <div>
                <Label className="text-white">Position Title</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="bg-gray-900 border-gray-700 text-white"
                  placeholder="Enter position title"
                />
              </div>
              
              <div>
                <Label className="text-white">Position Type</Label>
                <Select 
                  value={formData.type} 
                  onValueChange={(value) => handlePositionTypeChange(value, false)}
                >
                  <SelectTrigger className="bg-gray-900 border-gray-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700 max-h-[300px]">
                    {participantPositionTypes.participantOptions.length > 0 && (
                      <>
                        <div className="px-2 py-1.5 text-xs font-semibold text-orange-400 bg-gray-900">
                          From Assigned Participants
                        </div>
                        {participantPositionTypes.participantOptions.map(type => (
                          <SelectItem key={type.value} value={type.value} className="text-white">
                            {type.label}
                          </SelectItem>
                        ))}
                        <div className="my-1 border-t border-gray-600" />
                        <div className="px-2 py-1.5 text-xs font-semibold text-gray-400 bg-gray-900">
                          Standard ICS Positions
                        </div>
                      </>
                    )}
                    {participantPositionTypes.defaultOptions.map(type => (
                      <SelectItem key={type.value} value={type.value} className="text-white">
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-white">Assign Participant (Optional)</Label>
                <Select 
                  value={formData.participantId || "none"} 
                  onValueChange={(value) => setFormData({ ...formData, participantId: value === "none" ? "" : value })}
                >
                  <SelectTrigger className="bg-gray-900 border-gray-700 text-white">
                    <SelectValue placeholder="Select participant..." />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700">
                    <SelectItem value="none" className="text-gray-400">-- Leave Vacant --</SelectItem>
                    {exerciseParticipants.map(p => (
                      <SelectItem key={p.id} value={p.id} className="text-white">
                        {`${p.lastName || p.lastname || ''}, ${p.firstName || p.firstname || ''}`.trim() || p.name} {p.position ? `(${p.position})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowAddModal(false)} className="border-gray-600">
                  Cancel
                </Button>
                <Button onClick={handleSaveNew} className="bg-green-500 hover:bg-green-600 text-black">
                  Create Position
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <Card className="bg-gray-800 border-gray-700 w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle className="text-red-500">Delete Position</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-gray-300">
                Are you sure you want to delete this position? This will also remove all child positions.
              </p>
              <p className="text-red-400 font-semibold">
                {findPositionById(showDeleteConfirm)?.title}
              </p>
              
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowDeleteConfirm(null)} className="border-gray-600">
                  Cancel
                </Button>
                <Button onClick={confirmDelete} className="bg-red-500 hover:bg-red-600 text-white">
                  Delete Position
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      </>
      )}
    </div>
  );
};

export default ICSOrganizationChart;
